import { useMemo, useState, useEffect } from 'react';
import { Button, Col, Modal, Row, Spin, Tag } from 'antd';
import { fetchPayPackages, type PayPackage } from '../../api/pay';
import { usePayIntentStore } from '../payment/payIntent';
import { usePopupStore } from '../../stores/popupStore';

// B1：会员套餐订阅弹窗
// - 套餐数据实时取自 UBS（经 /pay/packages），后台可配展示层（名称/划线原价/角标/推荐/限时文案）
// - 「立即开通」→ 现有支付链路（payIntent → PayDialogHost）
const FEATURE_LABEL: Record<string, string> = {
  plc_basic: 'PLC 基础联动',
  plc_full: 'PLC 全量协议',
  '3d_basic': '3D 场景基础',
  '3d': '3D 高级能力',
  gis: 'GIS 全球地图',
  ai_basic: 'AI 助手基础',
  ai_full: 'AI 助手全量',
  private_deploy: '私有化部署',
  watermark: '画面水印',
};

export default function UpgradePopup() {
  const upgradeOpen = usePopupStore((s) => s.upgradeOpen);
  const cfg = usePopupStore((s) => s.config.upgrade);
  const closeUpgrade = usePopupStore((s) => s.closeUpgrade);

  const [packages, setPackages] = useState<PayPackage[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!upgradeOpen) return;
    setLoading(true);
    fetchPayPackages()
      .then((r) => setPackages(Array.isArray(r?.items) ? r.items : []))
      .catch(() => setPackages([]))
      .finally(() => setLoading(false));
  }, [upgradeOpen]);

  // 隐藏 free 档（无需购买）；按类型白名单过滤（排除边缘硬件等非订阅套餐）；测试套餐不进售卖弹窗
  const sellable = useMemo(() => {
    const types = cfg?.packageTypes?.length ? cfg.packageTypes : ['saas', 'private'];
    return packages
      .filter((p) => p.packageType !== 'free')
      .filter((p) => !p.packageType || types.includes(p.packageType))
      .filter((p) => Number(p.price) > 0) // 免费档（Free 0 元，packageType=saas）不进售卖弹窗
      .filter((p) => !/测试|联调/.test(p.name))
      .sort((a, b) => Number(a.price) - Number(b.price));
  }, [packages, cfg?.packageTypes]);

  const buy = (p: PayPackage) => {
    const ov = cfg?.packageOverrides?.[p.packageType ?? ''] ?? {};
    usePayIntentStore.getState().openPay(
      { orderType: 'subscription', packageId: p.id, subject: `套餐订阅-${ov.displayName || p.name}` },
      () => closeUpgrade(),
    );
  };

  return (
    <Modal
      open={upgradeOpen}
      onCancel={closeUpgrade}
      footer={null}
      centered
      width={920}
      styles={{ body: { padding: '20px 24px 16px' } }}
    >
      <div data-testid="upgrade-popup">
        <div style={{ textAlign: 'center', marginBottom: 6, fontSize: 20, fontWeight: 700, color: '#e8e8e8' }}>
          {cfg?.title || '会员套餐订阅'}
        </div>
        {cfg?.subtitle ? (
          <div style={{ textAlign: 'center', fontSize: 13, color: '#9a9aa8', marginBottom: 12 }}>{cfg.subtitle}</div>
        ) : null}
        {cfg?.promoText ? (
          <div style={{ textAlign: 'center', fontSize: 12, color: '#ffa940', marginBottom: 12 }}>{cfg.promoText}</div>
        ) : null}
        {cfg?.bannerUrl ? (
          <img src={cfg.bannerUrl} alt="活动" style={{ width: '100%', maxHeight: 150, objectFit: 'cover', borderRadius: 8, marginBottom: 14 }} />
        ) : null}

        <Spin spinning={loading}>
          <Row gutter={14}>
            {sellable.length === 0 && !loading ? (
              <Col span={24} style={{ textAlign: 'center', color: '#888', padding: '24px 0' }}>
                暂无在售套餐（请稍后重试或联系客服）
              </Col>
            ) : null}
            {sellable.map((p) => {
              const ov = cfg?.packageOverrides?.[p.packageType ?? ''] ?? {};
              const name = ov.displayName || p.name;
              const price = Number(p.price);
              const featured = ov.featured || cfg?.recommendPackageType === p.packageType;
              return (
                <Col span={sellable.length >= 3 ? 8 : 12} key={p.id}>
                  <div
                    data-testid={`upgrade-package-${p.packageType}`}
                    style={{
                      position: 'relative',
                      border: featured ? '1px solid #faad14' : '1px solid #333',
                      background: featured ? 'rgba(250,173,20,0.06)' : 'rgba(255,255,255,0.02)',
                      borderRadius: 10,
                      padding: '16px 16px 14px',
                      height: '100%',
                      display: 'flex',
                      flexDirection: 'column',
                    }}
                  >
                    {ov.badge ? (
                      <Tag color="gold" style={{ position: 'absolute', top: -10, right: 12 }}>{ov.badge}</Tag>
                    ) : null}
                    <div style={{ fontSize: 15, fontWeight: 600, color: '#e8e8e8' }}>{name}</div>
                    <div style={{ margin: '8px 0 2px' }}>
                      <span style={{ fontSize: 24, fontWeight: 700, color: featured ? '#ffc53d' : '#d9d9d9' }}>¥{price}</span>
                      <span style={{ fontSize: 12, color: '#8a8a99', marginLeft: 4 }}>
                        {p.validMonth > 0 ? `/ ${p.validMonth} 个月` : '/ 永久'}
                      </span>
                    </div>
                    {ov.originalPrice ? (
                      <div style={{ fontSize: 12, color: '#7a7a88', textDecoration: 'line-through' }}>原价 ¥{ov.originalPrice}</div>
                    ) : null}
                    <ul style={{ margin: '10px 0 12px', padding: 0, listStyle: 'none', flex: 1 }}>
                      {(p.featureList ?? []).map((f) => (
                        <li key={f} style={{ fontSize: 12, color: '#b8b8c8', lineHeight: 1.9 }}>· {FEATURE_LABEL[f] || f}</li>
                      ))}
                      {(ov.extraBenefits ?? []).map((f) => (
                        <li key={f} style={{ fontSize: 12, color: '#ffa940', lineHeight: 1.9 }}>· {f}</li>
                      ))}
                    </ul>
                    <Button type="primary" block data-testid={`upgrade-buy-${p.packageType}`} onClick={() => buy(p)}>
                      立即开通
                    </Button>
                  </div>
                </Col>
              );
            })}
          </Row>
        </Spin>

        <div style={{ marginTop: 14, fontSize: 11, color: '#7a7a88', textAlign: 'center' }}>
          开通即表示同意《服务协议》；支付成功后套餐权益即时生效，已有权益时长自动叠加。
        </div>
      </div>
    </Modal>
  );
}
